import React, { useEffect, useMemo, useRef, useState } from "react";
import { MessageCircle, Send, X, Loader2, ExternalLink, MapPin, Sparkles } from "lucide-react";
import { chatbotApi } from "../lib/api";
import { useUiString } from "../lib/useContent";

// -----------------------------------------------------------------------------
// ChatbotWidget — floating "Assistente AI" button + chat window.
//
// V8 features:
//   • Suggestion chips (fetched from /api/chatbot/suggestions)
//   • Conversation memory (last N turns sent with each request)
//   • Structured business_card blocks (image + name + category + address + CTA)
//   • Intelligent fallback with alternative categories
// -----------------------------------------------------------------------------

const SESSION_KEY = "ccn_chatbot_session_id";
const HISTORY_LIMIT = 10; // messages sent to backend for conversation memory

const getSessionId = () => {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = "s_" + Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch { return "s_" + Date.now().toString(36); }
};

const renderMarkdownInline = (text) => {
  const nodes = [];
  const re = /\[([^\]]{1,200})\]\((\/[^)\s]{0,200})\)/g;
  let last = 0; let m;
  while ((m = re.exec(text))) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    nodes.push(
      <a key={`l-${m.index}`} href={m[2]} className="text-[#12964A] underline hover:text-[#0E7C3D]">
        {m[1]}
      </a>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
};

const ChatbotWidget = () => {
  const [config, setConfig] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const sessionId = useMemo(() => getSessionId(), []);
  const scrollRef = useRef(null);
  const { value: errorFallback } = useUiString("chatbot.error_generic", "Errore temporaneo. Riprova più tardi.");

  useEffect(() => {
    let cancelled = false;
    chatbotApi.config()
      .then(r => { if (!cancelled) setConfig(r.data); })
      .catch(() => { if (!cancelled) setConfig({ enabled: false }); });
    return () => { cancelled = true; };
  }, []);

  // Fetch suggestions after the widget is enabled (avoid extra call when disabled)
  useEffect(() => {
    if (!config?.enabled) return;
    chatbotApi.suggestions().then(r => setSuggestions(r.data || [])).catch(() => setSuggestions([]));
  }, [config?.enabled]);

  useEffect(() => {
    if (open && config?.welcome_message && messages.length === 0) {
      setMessages([{ role: "assistant", text: config.welcome_message, isWelcome: true }]);
    }
  }, [open, config, messages.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  if (!config?.enabled) return null;

  const sendQuery = async (q) => {
    q = (q || "").trim();
    if (!q || sending) return;
    setMessages(prev => [...prev, { role: "user", text: q }]);
    setInput(""); setSending(true); setError(null);
    // Build history payload from prior conversation (skip welcome message)
    const historyPayload = messages
      .filter(m => !m.isWelcome)
      .slice(-HISTORY_LIMIT)
      .map(m => ({ role: m.role, text: m.text }));
    try {
      const r = await chatbotApi.ask(q, sessionId, historyPayload);
      setMessages(prev => [...prev, {
        role: "assistant",
        text: r.data.answer,
        links: r.data.links || [],
        blocks: r.data.blocks || [],
      }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || errorFallback;
      setMessages(prev => [...prev, { role: "assistant", text: detail, error: true }]);
      setError(detail);
    } finally { setSending(false); }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendQuery(input); }
  };

  // Show chips: on welcome, and after each assistant message with no results
  const showChipsNow = open && suggestions.length > 0 && messages.length <= 1;

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chatbot-open-btn"
          className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] text-white rounded-full pl-4 pr-5 py-3 shadow-lg shadow-black/10 transition-all hover:shadow-xl"
          aria-label={config.button_label}
        >
          <MessageCircle className="w-5 h-5" />
          <span className="text-sm font-medium">{config.button_label}</span>
          {config.mode === "preview" && (
            <span className="ml-1 text-[10px] uppercase tracking-wider bg-white/20 rounded-full px-2 py-0.5">Preview</span>
          )}
        </button>
      )}

      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-5 right-5 z-40 w-[min(440px,calc(100vw-2.5rem))] h-[min(660px,calc(100vh-6rem))] bg-white border border-[#E5E7EB] rounded-3xl shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="bg-[#12964A] text-white px-5 py-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm">{config.button_label}</div>
              <div className="text-[11px] opacity-80">Assistente {config.ccn_name || "CCN"}</div>
            </div>
            {config.mode === "preview" && (
              <span className="text-[10px] uppercase tracking-wider bg-white/20 rounded-full px-2 py-0.5">Preview</span>
            )}
            <button onClick={() => setOpen(false)} aria-label="Chiudi" data-testid="chatbot-close-btn" className="hover:bg-white/10 rounded-full p-1.5">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FAFBFC]">
            {messages.map((m, i) => (
              <Message key={i} m={m} />
            ))}
            {showChipsNow && (
              <div className="pt-1" data-testid="chatbot-suggestions">
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 mb-2">Domande suggerite</div>
                <div className="flex flex-wrap gap-1.5">
                  {suggestions.map((s) => (
                    <button
                      key={s.id || s.text}
                      onClick={() => sendQuery(s.text)}
                      data-testid={`suggestion-${(s.id || s.text).toString().slice(0, 20)}`}
                      className="text-xs bg-white border border-[#E5E7EB] hover:border-[#12964A] hover:bg-[#F0FAF3] text-slate-700 rounded-full px-3 py-1.5 transition-colors"
                    >
                      {s.text}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {sending && (
              <div className="flex items-center gap-2 text-slate-500 text-xs px-3">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Sto cercando…
              </div>
            )}
          </div>

          <div className="border-t border-[#E5E7EB] bg-white p-3">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder={config.placeholder}
                rows={1}
                disabled={sending}
                data-testid="chatbot-input"
                className="flex-1 resize-none rounded-2xl border border-[#E5E7EB] focus:border-[#12964A] focus:ring-2 focus:ring-[#F0FAF3] outline-none px-4 py-2.5 text-sm max-h-24"
              />
              <button
                onClick={() => sendQuery(input)}
                disabled={sending || !input.trim()}
                data-testid="chatbot-send-btn"
                aria-label="Invia"
                className="bg-[#12964A] hover:bg-[#0E7C3D] disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-full p-2.5 flex-shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <div className="text-[10px] text-slate-400 mt-2 text-center">
              Le risposte usano solo dati pubblicati nel portale.
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const Message = ({ m }) => {
  if (m.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] bg-[#12964A] text-white rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm whitespace-pre-wrap">
          {m.text}
        </div>
      </div>
    );
  }
  return (
    <div className="flex">
      <div className={`max-w-[92%] rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm ${m.error ? "bg-red-50 text-red-800 border border-red-100" : "bg-white text-[#1F2937] border border-[#F1F5F9]"}`}>
        <div className="whitespace-pre-wrap leading-relaxed">{renderMarkdownInline(m.text)}</div>
        {m.links?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {m.links.map((l, i) => (
              <a key={i} href={l.url} className="inline-flex items-center gap-1 text-xs bg-[#F0FAF3] text-[#0E7C3D] hover:bg-[#E1F3E8] rounded-full px-2.5 py-1">
                {l.label} <ExternalLink className="w-3 h-3" />
              </a>
            ))}
          </div>
        )}
        {m.blocks?.length > 0 && (
          <div className="mt-3 space-y-2" data-testid="chatbot-blocks">
            {m.blocks.map((b, i) => <ContentBlockRenderer key={i} block={b} />)}
          </div>
        )}
      </div>
    </div>
  );
};

// Renders a business card (V8). Unknown block types render nothing so future
// backend extensions don't cause visual regressions.
const ContentBlockRenderer = ({ block }) => {
  if (!block || !block.type) return null;
  if (block.type === "business_card") return <BusinessCard data={block.data || {}} />;
  // 'suggestions' block from fallback path is already rendered via markdown links —
  // silently skip to avoid duplication.
  return null;
};

const BusinessCard = ({ data }) => {
  const {
    name, category, short_description, address, cover_image_url, url, maps_url,
  } = data || {};
  if (!name || !url) return null;
  return (
    <div className="border border-[#E5E7EB] rounded-2xl overflow-hidden bg-white" data-testid="chatbot-business-card">
      {cover_image_url && (
        <div className="h-28 bg-slate-100 overflow-hidden">
          <img
            src={cover_image_url}
            alt={name}
            loading="lazy"
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </div>
      )}
      <div className="p-3">
        {category && (
          <div className="text-[10px] uppercase tracking-[0.15em] text-[#12964A] mb-1">{category}</div>
        )}
        <div className="font-semibold text-[#1F2937] text-sm leading-tight">{name}</div>
        {short_description && (
          <div className="text-xs text-slate-500 mt-1 line-clamp-2">{short_description}</div>
        )}
        {address && (
          <div className="text-xs text-slate-500 mt-1.5 flex items-start gap-1">
            <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>{address}</span>
          </div>
        )}
        <div className="flex gap-2 mt-2.5">
          <a href={url} className="inline-flex items-center gap-1 text-xs bg-[#12964A] hover:bg-[#0E7C3D] text-white rounded-full px-3 py-1.5">
            Apri scheda <ExternalLink className="w-3 h-3" />
          </a>
          {maps_url && (
            <a href={maps_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs bg-white border border-[#E5E7EB] hover:border-[#12964A] text-slate-700 rounded-full px-3 py-1.5">
              Apri su Maps <MapPin className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatbotWidget;
